/**
 * Fit to Fly Certificate — interactive form for populating fields
 * before generating the PDF.
 */
import { Input } from '@/frontend/components/ui/input'
import { ClinicHeaderPreview } from '@/frontend/features/printing/documents/blocks/clinic-header'
import { DocumentPreview } from '@/frontend/features/printing/documents/blocks/document-preview'
import {
  formatCalendarDate,
  formatSex,
  resolveDateFormat,
} from '@/frontend/features/printing/documents/blocks/format-utils'
import { VetSignoffPreview } from '@/frontend/features/printing/documents/blocks/vet-signoff'
import {
  certifyText,
  defaultFitToFlyFields,
  type FitToFlyFields,
  FitToFlyPdf,
} from '@/frontend/features/printing/documents/fit-to-fly.print'
import type { DocumentFormProps } from '@/frontend/features/printing/documents/types'
import { withDocumentEditor } from '@/frontend/features/printing/documents/with-document-editor'

function FitToFlyForm({ context, value, onChange }: DocumentFormProps<FitToFlyFields>) {
  const { patient, owner, clinic } = context
  const address = [owner.address1, owner.address2, owner.city, owner.postalCode]
    .filter(Boolean)
    .join(', ')
  // The preview is WYSIWYG with the PDF, so it follows the *document* rather than the
  // viewer: same resolution as the PDF half, off the owning member's region. Otherwise a
  // UK vet previewing a US member's certificate sees a different date order on screen
  // than in the file they download. DOB is a calendar day, so only the locale applies.
  const fmt = resolveDateFormat(null, owner.region)

  return (
    <DocumentPreview>
      <ClinicHeaderPreview clinic={clinic} />

      <h1 className="mb-6 text-center text-lg font-bold">FIT TO FLY CERTIFICATE</h1>

      {/* Owner */}
      <p>
        {owner.firstName} {owner.lastName}
      </p>
      {address && <p>{address}</p>}

      {/* Patient details */}
      <div className="mt-4 space-y-0.5">
        <p>
          Animal: <strong>{patient.name}</strong>
        </p>
        <p>
          Species: <strong>{patient.species}</strong>
        </p>
        <p>
          Breed: <strong>{patient.breed || '—'}</strong>
        </p>
        {patient.dateOfBirth && (
          <p>
            DOB: <strong>{formatCalendarDate(patient.dateOfBirth, fmt)}</strong>
          </p>
        )}
        <p>
          Sex: <strong>{formatSex(patient.sex, patient.isNeutered)}</strong>
        </p>
        <p className="flex items-center gap-2">
          Colour:
          <Input
            className="inline-flex h-7 w-40"
            value={value.colour}
            onChange={(e) => onChange({ ...value, colour: e.target.value })}
          />
        </p>
        {patient.microchipNumber && (
          <p>
            Microchip Number: <strong>{patient.microchipNumber}</strong>
          </p>
        )}
      </div>

      {/* Body */}
      <p className="mt-6">To whom it may concern,</p>
      <p className="mt-4">{certifyText(patient.species)}</p>
      <p className="mt-4">
        Please feel free to contact us for any further information or clarification.
      </p>

      <p className="mt-4">Sincerely,</p>

      <VetSignoffPreview
        vetName={value.vetName}
        onChange={(v) =>
          onChange({ ...value, vetName: v.vetName, vetLicenseNr: v.licenseNumber ?? '' })
        }
        label="Dr"
        showLicenseNumber
        showSignature
        showDate
      />

      <p className="mt-4">Snoots Vet, London</p>
    </DocumentPreview>
  )
}

export const fitToFly = withDocumentEditor({
  id: 'fit-to-fly',
  label: 'Fit to Fly Certificate',
  defaultFields: defaultFitToFlyFields,
  Preview: FitToFlyForm,
  Pdf: FitToFlyPdf,
})
